/**
 * 智能体冻结单元：agent + 派生生图工作卡 + 结果图片组 + 自动分组外框。
 *
 * 单元关联以 agent 节点数据为准（childNodeIds / groupNodeId / containerNodeId），
 * 与血缘边无关——边只是视觉表达，删边不影响单元判定（边删除保护见
 * connectionRules.isAgentLineageEdge + boardStore.removeEdges）。
 *
 * 冻结语义：
 *  - 删除：单元内任一节点（含外框）触发的删除手势 → 确认后整批删除
 *    （deleteNodesWithUnitGuard），取消则一个都不删
 *  - 拖动：整组联动（expandIdsWithUnits），拖任一成员全单元 + 外框一起移动
 *  - 外框：生成批次时自动创建并包裹全批（agentGenerator），删除计划项后
 *    按存活成员收缩（fitAgentContainer）
 * 存量批次（无 containerNodeId）没有外框，但删除守卫与边保护照常生效。
 */
import { boardActions, getBoardState } from '../state/boardStore';
import { boardConfirm } from '../components/dialogs/BoardConfirm';
import { makeNode } from '../utils';
import type { AgentNodeData, CanvasNode, GroupNodeData } from '../types';

export interface AgentUnit {
  agentId: string;
  /** 分组外框节点 id（存量批次可能为 null） */
  containerId: string | null;
  /** 单元成员：agent + 存活工作卡 + 结果组（不含外框本身） */
  memberIds: string[];
}

function readAgentData(node: CanvasNode | undefined): AgentNodeData | null {
  return node && node.type === 'agent' ? (node.data as AgentNodeData) : null;
}

/** 节点是否属于某智能体批次（agent 本体 / 工作卡 / 结果组 / 外框容器） */
export function findAgentUnitOf(nodeId: string, nodes: CanvasNode[]): AgentUnit | null {
  for (const n of nodes) {
    const data = readAgentData(n);
    if (!data) continue;
    const isMember =
      n.id === nodeId ||
      (data.childNodeIds ?? []).includes(nodeId) ||
      data.groupNodeId === nodeId ||
      data.containerNodeId === nodeId;
    if (!isMember) continue;
    const wanted = new Set<string>([n.id, ...(data.childNodeIds ?? [])]);
    if (data.groupNodeId) wanted.add(data.groupNodeId);
    return {
      agentId: n.id,
      containerId: data.containerNodeId ?? null,
      memberIds: nodes.filter((x) => wanted.has(x.id)).map((x) => x.id),
    };
  }
  return null;
}

/** 把 id 集合扩展为完整智能体单元（含外框容器）：命中任一成员即并入整批 */
export function expandIdsWithUnits(ids: string[], nodes: CanvasNode[]): string[] {
  const result = new Set(ids);
  for (const n of nodes) {
    const data = readAgentData(n);
    if (!data) continue;
    const unit = [n.id, ...(data.childNodeIds ?? [])];
    if (data.groupNodeId) unit.push(data.groupNodeId);
    if (data.containerNodeId) unit.push(data.containerNodeId);
    if (unit.some((id) => result.has(id))) unit.forEach((id) => result.add(id));
  }
  return [...result];
}

/**
 * 删除手势统一入口：选中内容涉及智能体批次时，确认后整批删除（含外框，
 * 单次 removeNodes = 单条撤销历史），取消则不删任何节点；不涉及批次时
 * 维持原有直接删除。
 */
export async function deleteNodesWithUnitGuard(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const { nodes } = getBoardState();
  const expanded = expandIdsWithUnits(ids, nodes);
  if (expanded.length === ids.length) {
    boardActions.removeNodes(ids);
    return;
  }
  const idSet = new Set(expanded);
  const agentCardIds = new Set<string>();
  nodes.forEach((n) => {
    const data = readAgentData(n);
    (data?.childNodeIds ?? []).forEach((id) => agentCardIds.add(id));
  });
  const cardCount = nodes.filter((n) => idSet.has(n.id) && agentCardIds.has(n.id)).length;
  const ok = await boardConfirm({
    title: '删除整个智能体批次？',
    message: `所选节点属于智能体批次，将连同整个批次一起删除（智能体、${cardCount} 张生图工作卡与结果图片组），已从组内取出的图片会保留。`,
  });
  if (!ok) return;
  boardActions.removeNodes(expanded);
}

/** 按存活成员包围盒 + padding 适配外框位置尺寸；与现值相差 <1px 时跳过写入
 *  （挂载自愈校正不应造成无谓 dirty 与自动保存） */
export function fitAgentContainer(agentId: string): void {
  const { nodes } = getBoardState();
  const agent = nodes.find((n) => n.id === agentId);
  const data = readAgentData(agent);
  if (!data?.containerNodeId) return;
  const container = nodes.find((n) => n.id === data.containerNodeId);
  if (!container) return;
  const wanted = new Set<string>([agentId, ...(data.childNodeIds ?? [])]);
  if (data.groupNodeId) wanted.add(data.groupNodeId);
  const members = nodes.filter((n) => wanted.has(n.id));
  if (members.length === 0) return;
  const PAD = 40;
  const minX = Math.min(...members.map((n) => n.x)) - PAD;
  const minY = Math.min(...members.map((n) => n.y)) - PAD;
  const maxX = Math.max(...members.map((n) => n.x + n.width)) + PAD;
  const maxY = Math.max(...members.map((n) => n.y + n.height)) + PAD;
  const next = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  if (
    Math.abs(container.x - next.x) < 1 &&
    Math.abs(container.y - next.y) < 1 &&
    Math.abs(container.width - next.width) < 1 &&
    Math.abs(container.height - next.height) < 1
  ) {
    return;
  }
  boardActions.updateNode(data.containerNodeId, next);
  boardActions.commitNodeMutation();
}

/**
 * 外框自愈：批次成员存活但外框缺失（冻结外框上线前的存量批次）→ 补建外框
 * 并回写 containerNodeId（单条 applyGraphPatch 历史）；外框已存在 → 仅按
 * 实际尺寸校正。智能体挂载时调用一次，落库后不再产生变更。
 */
export function ensureAgentContainer(agentId: string): void {
  const { nodes } = getBoardState();
  const agent = nodes.find((n) => n.id === agentId);
  const data = readAgentData(agent);
  if (!agent || !data) return;
  const memberIds = [
    agentId,
    ...(data.childNodeIds ?? []),
    ...(data.groupNodeId ? [data.groupNodeId] : []),
  ].filter((id) => nodes.some((n) => n.id === id));
  // 没有批次产物（仅规划未生成）的智能体不需要外框
  if (memberIds.length <= 1) return;
  if (data.containerNodeId && nodes.some((n) => n.id === data.containerNodeId)) {
    fitAgentContainer(agentId);
    return;
  }
  const members = nodes.filter((n) => memberIds.includes(n.id));
  const PAD = 40;
  const minX = Math.min(...members.map((n) => n.x)) - PAD;
  const minY = Math.min(...members.map((n) => n.y)) - PAD;
  const maxX = Math.max(...members.map((n) => n.x + n.width)) + PAD;
  const maxY = Math.max(...members.map((n) => n.y + n.height)) + PAD;
  const containerNode = makeNode<GroupNodeData>(
    'group',
    { x: minX, y: minY },
    { label: '智能体批次', _autoGenerated: true },
    { width: maxX - minX, height: maxY - minY },
    -1
  );
  boardActions.applyGraphPatch({
    addNodes: [containerNode],
    nodeDataPatches: [{ id: agentId, data: { containerNodeId: containerNode.id } }],
  });
}

/** 图片组是否为某智能体批次的结果组（删空/取空守卫用） */
export function isAgentResultGroup(groupNodeId: string, nodes: CanvasNode[]): boolean {
  return nodes.some(
    (n) => n.type === 'agent' && (n.data as AgentNodeData).groupNodeId === groupNodeId
  );
}
